import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { enumChargesDistributionType } from '@app/data-layer/enums/entry-summaries';
import { ShipmentModel, ValidationMessageModel } from '@app/data-layer/models/common';
import { EntrySummaryModel } from '@app/data-layer/models/entry-summaries';
import { FREIGHT_COST_LESS_THAN_LINES_COUNT, FREIGHT_COST_MISSING } from '@constants/entity-validation-messages';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { CustomValidator } from '@helpers/custom-validator';
import { EntrySummaryValidationHelper } from '@helpers/entry-summary-validation.helper';
import { FormHelper } from '@helpers/form.helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { TranslateService } from '@ngx-translate/core';
import { EntrySummaryStateService, EsOcrQuickFixService, EsValidationsHelperService } from '@shared/es-mutual/services';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { ReplaySubject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-es-ocr-quick-fix-shipping-tos',
  templateUrl: './es-ocr-quick-fix-shipping-tos.component.html',
  styleUrls: ['./es-ocr-quick-fix-shipping-tos.component.scss'],
})
export class EsOcrQuickFixShippingTosComponent implements OnInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;

  @Input() public shipmentId: number;

  @Input() public shippingTosFields: ISectionFormField[];

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public entrySummary: EntrySummaryModel;
  public shipment: ShipmentModel;
  public formGroup: UntypedFormGroup;
  public displayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm };

  public typeControl: UntypedFormControl;
  public blControlTypes = [enumChargesDistributionType.COLLECT, enumChargesDistributionType.PREPAID];
  public enumChargesDistributionType: typeof enumChargesDistributionType = enumChargesDistributionType;
  public currencyInputMap: InputTypeMap = INPUT_TYPES_MAP.CURRENCY;
  public isShowingTosEditBlock: boolean = false;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private fb: FormBuilder,
    private translateService: TranslateService,
    private esOcrQuickFixService: EsOcrQuickFixService,
    private entrySummaryStateService: EntrySummaryStateService,
    private esValidationsHelperService: EsValidationsHelperService,
  ) {}

  ngOnInit(): void {
    this.entrySummary = this.esOcrQuickFixService.getEntrySummary();
    this.shipment = this.entrySummary?.shipments.find((shipment) => shipment.id === this.shipmentId);

    this.initializeFormGroup();

    this.setFieldsProperties();

    this.populateDisplayErrors(this.formGroup);
    this.createFormErrors();

    const isFormBlockEmpty = this.isAnyFormBlockFieldEmpty(this.shippingTosFields, this.formGroup);
    this.onShippingTermsBlockEdit(isFormBlockEmpty);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public blTypeControlId(type: enumChargesDistributionType): string {
    switch (type) {
      case enumChargesDistributionType.COLLECT:
        return 'bl-type-importer';
      case enumChargesDistributionType.PREPAID:
        return 'bl-type-exporter';
      default:
        return '';
    }
  }

  public blTypeControlLabel(type: enumChargesDistributionType): string {
    switch (type) {
      case enumChargesDistributionType.COLLECT:
        return 'ENTITY.IMPORTER';
      case enumChargesDistributionType.PREPAID:
        return 'LABELS.EXPORTER';
      default:
        return '';
    }
  }

  public blTypeControlHint(type: enumChargesDistributionType): string {
    switch (type) {
      case enumChargesDistributionType.COLLECT:
        return 'LABELS.IMPORTER_HINT';
      case enumChargesDistributionType.PREPAID:
        return 'LABELS.EXPORTER_HINT';
      default:
        return '';
    }
  }

  public get termsValidationMessages(): ValidationMessageModel[] {
    const termsMissingFields = [FREIGHT_COST_MISSING, FREIGHT_COST_LESS_THAN_LINES_COUNT];
    const allMessages = EntrySummaryValidationHelper.getValidationMessages(
      this.shipment,
      this.entrySummary?.transportationMode,
      true,
      ['SHIPMENTS'],
      true,
    );
    return allMessages.filter((m) => termsMissingFields.some((key) => m.text.endsWith(key)));
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[], fg: UntypedFormGroup): boolean {
    let isFgInvalid: boolean = false;
    Object.keys(fg.controls).forEach((key) => {
      if (fg.controls[key].invalid) {
        isFgInvalid = true;
        return;
      }
    });

    let invalidFields = blockFields.find((field) => fg.get(field.name)?.invalid || isFgInvalid);
    return !!invalidFields;
  }

  public onShippingTermsBlockEdit(isShowEditBlock: boolean) {
    this.isShowingTosEditBlock = isShowEditBlock;

    const chargesDistributionTypeField = this.shippingTosFields.find((field) => field.name === 'chargesDistributionType');
    if (isShowEditBlock) {
      chargesDistributionTypeField.isHidden = true;
    } else {
      chargesDistributionTypeField.isHidden = false;
    }
  }

  public onSubmit(needValidate: boolean = true) {
    if (needValidate && this.formGroup.invalid) {
      const invalidFields = this.fh.validateAllFields(this.formGroup);
      this.missingFieldsModal.openCustomModal(
        this.esValidationsHelperService.getEsSummaryModalModifiedData(this.entrySummary.transportationMode, invalidFields),
      );
      return;
    }

    this.entrySummaryStateService
      .updateShipment(this.shipment, this.formGroup.value)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.dataSaved.emit(this.formGroup.value);
      });
  }

  private initializeFormGroup() {
    this.formGroup = this.fb.group({
      transportationCharge: new UntypedFormControl(null, CustomValidator.updateControl('freightCost')),
      freightCost: new UntypedFormControl(null, [Validators.required]),
      chargesDistributionType: new UntypedFormControl(enumChargesDistributionType.PREPAID, CustomValidator.updateControl('freightCost')),
      insurance: new UntypedFormControl(null, CustomValidator.updateControl('freightCost')),
    });

    this.typeControl = this.formGroup.get('chargesDistributionType') as UntypedFormControl;

    this.typeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((type) => {
      this.syncFieldsVisibilityPerBlType(type);
    });
    this.formGroup.patchValue(this.shipment);

    if (this.formGroup.invalid) {
      this.formGroup.markAsDirty();
      this.formGroup.markAllAsTouched();
    }
  }

  private setFieldsProperties() {
    this.shippingTosFields.forEach((field) => {
      switch (field.name) {
        case 'chargesDistributionType':
          const translationLabel = 'CHARGES_DISTRIBUTION_TYPE.' + enumChargesDistributionType[this.shipment.chargesDistributionType];

          field.value = this.translateService.instant(translationLabel);
          if (this.isShowingTosEditBlock) {
            field.isHidden = true;
          } else {
            field.isHidden = false;
          }
          break;

        case 'freightCost':
        case 'transportationCharge':
        case 'insurance':
          field.value = String(this.shipment?.[field.name] ?? 0);
          break;

        default:
          break;
      }
    });
  }

  private populateDisplayErrors(fg: UntypedFormGroup) {
    Object.keys(fg.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'freightCost':
          this.displayErrors.set(fieldName, this.fieldMissingErrorText(fieldName, fg));
          break;
        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.formErrors = {
      transportationCharge: {
        required: 'FORM_ERROR.REQUIRED',
      },
      freightCost: {
        required: 'FORM_ERROR.REQUIRED',
      },
      chargesDistributionType: {
        required: 'FORM_ERROR.REQUIRED',
      },
      insurance: {
        required: 'FORM_ERROR.REQUIRED',
      },
    };
  }

  private fieldMissingErrorText(fieldName: string | string[], fg: UntypedFormGroup): string {
    return !fg.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private syncFieldsVisibilityPerBlType(type: enumChargesDistributionType) {
    switch (type) {
      case enumChargesDistributionType.COLLECT:
        this.shippingTosFields.forEach((field) => {
          if (field.name === 'insurance' || field.name === 'transportationCharge') {
            field.isHidden = true;
          }
          if (field.name === 'freightCost') {
            field.label = 'FORM_LABEL.TOTAL_CHARGES';
            field.isShowHelper = false;
          }
        });
        break;
      case enumChargesDistributionType.PREPAID:
        this.shippingTosFields.forEach((field) => {
          if (field.name === 'insurance' || field.name === 'transportationCharge') {
            field.isHidden = false;
          }
          if (field.name === 'freightCost') {
            field.label = 'FORM_LABEL.FREIGHT_COST';
            field.isShowHelper = true;
          }
        });
        break;
      default:
        break;
    }
  }
}
